import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../shared/_services/json-object-factory.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { EcConfigurationService } from './ec-configuration.service';

@Component({
    selector: 'gtw-ec-configuration',
    templateUrl: './ec-configuration.component.html',
    styleUrls: ['./ec-configuration.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EcConfigurationComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  minimise = false;

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  rowData: any = {};
  headerData: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  SERVER_CONFIG: any = {};

  // Component properties
  configurationForm: UntypedFormGroup;
  crudLoading = false;
  modalTitle = "EC Configuration";
  
  // Year picker functionality
  fromYear: number | null = null;
  toYear: number | null = null;
  showFromYearPicker: boolean = false;
  showToYearPicker: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private jsonObjectFactoryService: JsonObjectFactoryService,
    private gtwSnackbarService: GtwSnackbarService,
    private ecConfigurationService: EcConfigurationService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {
    this.configurationForm = this.fb.group({
      CONFIG_ITEM: ['', Validators.required],
      CONFIG_VALUE: [''],
      FROM_YEAR: ['', Validators.required],
      TO_YEAR: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.headerData = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  init(): void {
    if (this.filterParams && Object.keys(this.filterParams).length > 0) {
      this.initializeForm();
    }
  }

  private initializeForm(): void {
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      // Update mode - populate form with existing data
      // Format FROM_YEAR and TO_YEAR as YYYY values
      const fromYearFormatted = this.formatYearToYYYY(this.rowData.FROM_YEAR);
      const toYearFormatted = this.formatYearToYYYY(this.rowData.TO_YEAR);
      
      this.configurationForm.patchValue({
        CONFIG_ITEM: this.rowData.CONFIG_ITEM || '',
        CONFIG_VALUE: this.rowData.CONFIG_VALUE || '',
        FROM_YEAR: fromYearFormatted,
        TO_YEAR: toYearFormatted
      });
      
      // Set Year Picker values for edit mode with proper validation
      if (fromYearFormatted) {
        const fromYear = parseInt(fromYearFormatted);
        if (!isNaN(fromYear) && fromYear >= 1900 && fromYear <= 9999) {
          this.fromYear = fromYear;
          console.log('initializeForm - Setting fromYear to:', this.fromYear);
        } else {
          this.fromYear = null;
          console.log('initializeForm - fromYear is NaN or out of range');
        }
      } else {
        this.fromYear = null;
        console.log('initializeForm - fromYearFormatted is empty');
      }
      
      if (toYearFormatted) {
        const toYear = parseInt(toYearFormatted);
        if (!isNaN(toYear) && toYear >= 1900 && toYear <= 9999) {
          this.toYear = toYear;
        } else {
          this.toYear = null;
        }
      } else {
        this.toYear = null;
      }
    } else {
      // Create mode - set default values
      const taxYear = this.filterParams?.tax_year || new Date().getFullYear();
      const taxYearNum = parseInt(taxYear) || new Date().getFullYear();
      
      this.configurationForm.patchValue({
        FROM_YEAR: (taxYearNum - 1).toString(),
        TO_YEAR: '9999'
      });
      
      // Set Year Picker default values for create mode
      this.fromYear = taxYearNum - 1;
      this.toYear = 9999;
    }
  }

  onConfigItemChange(): void {
    const value = this.configurationForm.get('CONFIG_ITEM')?.value;
    if (value) {
      this.configurationForm.patchValue({ CONFIG_ITEM: value.toUpperCase() });
    }
  }

  isValid(): boolean {
    const form = this.configurationForm;
    if (!form.valid) {
      // Mark all fields as touched to show validation errors
      Object.keys(form.controls).forEach(key => {
        form.get(key)?.markAsTouched();
      });
      return false;
    }
    return true;
  }

  save(): void {
    if (!this.isValid()) {
      this.gtwSnackbarService.openSnackBar('Please fill all the required fields', 'gtw-snackbar--red');
      return;
    }

    this.crudLoading = true;
    
    // Build save data with real rowData values
    const saveData = this.buildSaveData();
    
    // 🚀 CRITICAL: Pass filterParams and rowData to service
    this.ecConfigurationService.saveConfigurationForm(
      this.GENERAL_CONFIG.base_url,  // 🚀 Use base_url (contains /gtw/)
      saveData,
      this.globalService?.globalParams ?? {},
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          // 🚀 CRITICAL: Show snackBar and emit 'refresh' string
          this.gtwSnackbarService.openSnackBar('EC Configuration created successfully', 'gtw-snackbar--green');
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  update(): void {
    if (!this.isValid()) {
      this.gtwSnackbarService.openSnackBar('Please fill all the required fields', 'gtw-snackbar--red');
      return;
    }

    this.crudLoading = true;
    
    // Build save data with real rowData values
    const saveData = this.buildSaveData();
    
    // 🚀 CRITICAL: Pass filterParams and rowData to service
    this.ecConfigurationService.updateConfigurationForm(
      this.GENERAL_CONFIG.base_url,  // 🚀 Use base_url (contains /gtw/)
      saveData,
      this.globalService?.globalParams ?? {},
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          // 🚀 CRITICAL: Show snackBar and emit 'refresh' string
          this.gtwSnackbarService.openSnackBar('EC Configuration updated successfully', 'gtw-snackbar--green');
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while updating. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  buildSaveData(): any {
    const formValue = this.configurationForm.value;
    const isUpdate = this.isUpdateMode();
    
    if (isUpdate) {
      // Update payload structure
      return {
        CONFIG_ITEM: formValue.CONFIG_ITEM || '',
        CONFIG_VALUE: formValue.CONFIG_VALUE || '',
        FROM_YEAR: parseInt(formValue.FROM_YEAR) || null,
        TO_YEAR: parseInt(formValue.TO_YEAR) || null,
        LAST_UPDATED_DATE: {
          timeZone: "America/New_York",
          length: 13,
          bytes: "eH0KFA4kGwAAAACBkA==",
          null: false,
          stream: {}
        },
        LAST_UPDATED_BY: this.USER_SETTINGS?.user?.sso_id || '',
        object_id: this.rowData?.object_id || '',
        SCENARIO_TYPE_CODE: this.globalService?.globalParams?.scenarioCode || '',
        tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || new Date().getFullYear().toString(),
        IS_UPDATE: "True"
      };
    } else {
      // Create payload structure
      return {
        CONFIG_ITEM: formValue.CONFIG_ITEM || '',
        CONFIG_VALUE: formValue.CONFIG_VALUE || '',
        FROM_YEAR: parseInt(formValue.FROM_YEAR) || null,
        TO_YEAR: parseInt(formValue.TO_YEAR) || null,
        FROM_YEAR_CLICK: false
      };
    }
  }

  convertYearToDate(year: any): string {
    if (!year) return '';
    
    // If it's already a date string, return as is
    if (typeof year === 'string' && year.includes('-')) {
      return year;
    }
    
    // Convert year number to date string (YYYY-01-01 format)
    const yearNum = parseInt(year.toString());
    if (!isNaN(yearNum)) {
      return `${yearNum}-01-01`;
    }
    
    return '';
  }

  formatYear(year: any): string {
    if (!year) return '';
    
    // If it's a date string, extract the year
    if (typeof year === 'string' && year.includes('-')) {
      return new Date(year).getFullYear().toString();
    }
    
    // If it's a number, convert to string
    if (typeof year === 'number') {
      return year.toString();
    }
    
    if (year instanceof Date) {
      return year.getFullYear().toString();
    }
    
    return year.toString();
  }

  formatYearToYYYY(year: any): string {
    if (!year) return '';
    
    // If it's already a 4-digit year string, return as is
    if (typeof year === 'string' && year.length === 4 && /^\d{4}$/.test(year)) {
      return year;
    }
    
    // If it's an ISO date string with timezone (like 2020-12-31T18:30:00.000Z)
    // Parse the date in local timezone and extract the year
    if (typeof year === 'string' && year.includes('T') && year.includes('Z')) {
      const dateObj = new Date(year);
      // Use local time to get the year
      const localYear = dateObj.getFullYear();
      console.log('ISO date string detected:', year);
      console.log('Local year:', localYear);
      return localYear.toString();
    }
    
    // If it's a date string (YYYY-MM-DD format), extract the year
    if (typeof year === 'string' && year.includes('-')) {
      const yearPart = year.split('-')[0];
      if (yearPart.length === 4 && /^\d{4}$/.test(yearPart)) {
        return yearPart;
      }
    }
    
    // If it's a number, convert to 4-digit string
    if (typeof year === 'number') {
      const yearStr = year.toString();
      if (yearStr.length === 4) {
        return yearStr;
      } else if (yearStr.length < 4) {
        // Pad with leading zeros if needed
        return yearStr.padStart(4, '0');
      }
    }
    
    // If it's a Date object, extract the year in local timezone
    if (year instanceof Date) {
      return year.getFullYear().toString();
    }
    
    // Try to parse as number and format as 4-digit year
    const yearNum = parseInt(year.toString());
    if (!isNaN(yearNum)) {
      // For years < 100, treat as 2-digit years (2000-2099)
      if (yearNum >= 1000 && yearNum <= 9999) {
        return yearNum.toString();
      } else if (yearNum >= 0 && yearNum < 100) {
        // Treat 2-digit years as 2000s
        return (2000 + yearNum).toString();
      }
    }
    
    // If all else fails, return empty string
    return '';
  }

  reset(): void {
    this.configurationForm.reset();
    this.fromYear = null;
    this.toYear = null;
    this.showFromYearPicker = false;
    this.showToYearPicker = false;
    this.initializeForm();
  }

  cancel(): void {
    // AngularJS handles modal close automatically via event listener
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-ec-configuration-ce')) {
      document.querySelector('gtw-ec-configuration-ce')?.dispatchEvent(updateEvent);
    }
  }

  toggleMinimize(): void {
    this.minimise = !this.minimise;
  }

  // Year Picker methods
  openFromYearPicker(): void {
    this.showFromYearPicker = true;
    if (this.showToYearPicker) {
      this.showToYearPicker = false;
    }
  }

  closeFromYearPicker(): void {
    this.showFromYearPicker = false;
  }

  openToYearPicker(): void {
    this.showToYearPicker = true;
    if (this.showFromYearPicker) {
      this.showFromYearPicker = false;
    }
  }

  closeToYearPicker(): void {
    this.showToYearPicker = false;
  }

  onFromYearSelected(year: number | null): void {
    if (year === null) {
      this.fromYear = null;
      this.configurationForm.get('FROM_YEAR')?.setValue('');
    } else {
      this.fromYear = year;
      this.configurationForm.get('FROM_YEAR')?.setValue(year.toString());
      
      // Validate that From Year is not greater than To Year
      if (this.toYear && year > this.toYear) {
        this.gtwSnackbarService.openSnackBar('From Year cannot be greater than To Year', 'gtw-snackbar--red');
        this.fromYear = null;
        this.configurationForm.get('FROM_YEAR')?.setValue('');
      }
    }
  }

  onToYearSelected(year: number | null): void {
    if (year === null) {
      this.toYear = null;
      this.configurationForm.get('TO_YEAR')?.setValue('');
    } else {
      this.toYear = year;
      this.configurationForm.get('TO_YEAR')?.setValue(year.toString());
      
      // Validate that To Year is not less than From Year
      if (this.fromYear && year < this.fromYear) {
        this.gtwSnackbarService.openSnackBar('To Year cannot be less than From Year', 'gtw-snackbar--red');
        this.toYear = null;
        this.configurationForm.get('TO_YEAR')?.setValue('');
      }
    }
  }

  trackByIndex(index: number, item: any): any {
    return index;
  }

  isUpdateMode(): boolean {
    return this.rowData && Object.keys(this.rowData).length > 0;
  }
}

